import React, { memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import LanguageDropdown from 'Layout/AuthLayout/HeaderDropdown/LanguageDropdown';
import MenuDropdown from 'Layout/AuthLayout/HeaderDropdown/MenuDropdown';
import ProfileMenu from 'Layout/AuthLayout/HeaderDropdown/ProfileMenu';

import { checkHasPermission, onClickLink } from 'utils/permission';

import { ReactComponent as ArrowHeaderIcon } from 'assets/images/headerNavigation/arrow.svg';
import { ReactComponent as EyeIcon } from 'assets/images/eye-icon.svg';
import images from 'assets/images';
import { LOCATIONS } from 'constants/index';
import headerNavigation from 'constants/headerNavigation';

import styles from './Header.module.scss';

const Header = (props) => {
  const {
    myPermission,
    placeDetail,
  } = props;

  const { t } = useTranslation();
  const location = useLocation();

  const searchQuery = React.useMemo(() => {
    const params = new URLSearchParams(location.search);

    return {
      place: params.get('place'),
    };
  }, [location]);

  const isLocationDetail = React.useMemo(
    () => location.pathname.startsWith('/locations/detail'),
    [location.pathname],
  );

  const onPreviewPlace = React.useCallback(() => {
    if (placeDetail?.slug || placeDetail?._id || placeDetail?.id) {
      const slug = placeDetail.slug || placeDetail._id || placeDetail.id;
      const baseUrl = process.env.REACT_APP_BOOKESE_URL || 'https://bookese.wiicamp.xyz';
      window.open(`${baseUrl}/${slug}`, '_blank', 'noopener,noreferrer');
    }
  }, [placeDetail]);

  const headerRef = React.useRef();
  // const buttonToggleMenuRef = React.useRef();
  const navigationRef = React.useRef();
  const rightHeaderRef = React.useRef();

  const [isShowTitle, setIsShowTitle] = React.useState(true);
  const [isBreakTitle, setIsBreakTitle] = React.useState(false);
  const isBreakTitleRef = React.useRef(false);
  const naturalNavWidthRef = React.useRef(0);

  const onToggleFullscreen = React.useCallback(() => {
    if (
      !document.fullscreenElement
      /* alternative standard method */ && !document.mozFullScreenElement
      && !document.webkitFullscreenElement
    ) {
      // current working methods
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      } else if (document.documentElement.mozRequestFullScreen) {
        document.documentElement.mozRequestFullScreen();
      } else if (document.documentElement.webkitRequestFullscreen) {
        document.documentElement.webkitRequestFullscreen(
          Element.ALLOW_KEYBOARD_INPUT,
        );
      }
    } else if (document.cancelFullScreen) {
      document.cancelFullScreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitCancelFullScreen) {
      document.webkitCancelFullScreen();
    }
  }, []);

  const updateDimensions = React.useCallback(() => {
    const nav = navigationRef.current;

    if (!nav) {
      return;
    }

    const width = window.innerWidth;
    setIsShowTitle(width > 1080 || (width <= 992 && width > 810));

    const availableWidth = nav.clientWidth;
    if (!isBreakTitleRef.current) {
      naturalNavWidthRef.current = nav.scrollWidth;
    }

    const naturalWidth = naturalNavWidthRef.current || nav.scrollWidth;
    const shouldBreak = naturalWidth > availableWidth + 1;

    isBreakTitleRef.current = shouldBreak;
    setIsBreakTitle(shouldBreak);
  }, []);

  // const onToggleSidebar = React.useCallback(() => {
  //   const { body } = document;
  //   body.classList.toggle('sidebar-enable');

  //   updateDimensions();
  // }, []);

  const checkShowMenu = React.useMemo(() => {
    if (location.pathname.includes(LOCATIONS.LOCATION_DETAIL.DEFAULT)) {
      return true;
    }

    return false;
  }, [location.pathname]);

  const onScrollElement = React.useCallback((item) => {
    if (item) {
      const currentPosition = item.offsetTop;

      if (currentPosition > window.innerHeight) {
        headerRef.current.getScrollElement().scrollTop = currentPosition - 300;
      }
    }
  }, []);

  const onActivateParentDropdown = React.useCallback((item) => {
    item.classList.add('active');

    const parent = item.parentElement;
    const parent2El = parent.childNodes[1];

    if (parent2El && parent2El.id !== 'horizontal-menu' && parent.id !== 'horizontal-menu') {
      parent2El.classList.add('active');
    }

    if (parent) {
      // parent.classList.add('mm-active');
      const parent2 = parent.parentElement;

      if (parent2 && parent2.id !== 'navbar-header-menu') {
        parent2.classList.add('active');
      }

      onScrollElement(item);

      return false;
    }

    onScrollElement(item);

    return false;
  }, []);

  const initMenu = React.useCallback(() => {
    let matchingMenuItem = null;

    const ul = document.querySelector('#horizontal-menu');
    const items = ul.getElementsByTagName('a');

    const pathName = location.pathname;
    const route = `/${pathName.split('/')[1]}`;

    for (let i = 0; i < items.length; i += 1) {
      if (pathName === items[i].pathname || route === items[i].pathname) {
        matchingMenuItem = items[i];
        break;
      }
    }

    if (matchingMenuItem) {
      onActivateParentDropdown(matchingMenuItem);
    }
  }, []);

  const onClickLinkItem = React.useCallback((e) => {
    onClickLink(e, myPermission);
  }, [myPermission]);

  React.useLayoutEffect(() => {
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [updateDimensions]);

  React.useLayoutEffect(() => {
    isBreakTitleRef.current = false;
    naturalNavWidthRef.current = 0;
    updateDimensions();
  }, [checkShowMenu, updateDimensions]);

  React.useEffect(() => {
    if (checkShowMenu) initMenu();
  }, [location.pathname]);

  return (
    <header id="page-topbar" className={styles.Component}>
      <div className="navbar-header" ref={headerRef}>
        <div className="d-flex" id="navbar-header-menu">
          <div className="navbar-brand-box d-lg-none d-block">
            <Link to={LOCATIONS.HOME_PAGE} className="logo logo-dark">
              <span className="logo-sm">
                <img
                  src={images.logoBookeseFull.default}
                  alt=""
                  width={40}
                  height={40}
                />
              </span>
            </Link>

            <Link to={LOCATIONS.HOME_PAGE} className="logo logo-light">
              <span className="logo-sm">
                <img src={images.logoBookeseMini.default} alt="" height="40" />
              </span>
            </Link>
          </div>

          {/* <button
            type="button"
            onClick={onToggleSidebar}
            className="btn btn-sm px-3 font-size-16 header-item "
            id="vertical-menu-btn"
            ref={buttonToggleMenuRef}
            aria-label="Toggle sidebar menu"
          >
            <i className="fa fa-fw fa-bars" />
          </button> */}

          {
            checkShowMenu && (
              <div className="Navigation" ref={navigationRef} id="horizontal-menu">
                {
                  headerNavigation.map((item) => {
                    const IconComponent = item.icon;

                    if (item.children?.length > 0) {
                      return (
                        <MenuDropdown
                          list={item.children}
                          key={item.name}
                          classnames="Navigation__Item"
                          isBreakTitle={isBreakTitle && isShowTitle}
                        >
                          <span
                            className="Navigation__Item__Icon pointer-events-none"
                          >
                            <IconComponent />
                          </span>

                          {isShowTitle ? (
                            <span className="Navigation__Item__Label pointer-events-none">
                              <span className="Navigation__Item__Title">{t(item.name)}</span>
                              <span className="Navigation__Item__Arrow">
                                <ArrowHeaderIcon />
                              </span>
                            </span>
                          ) : (
                            <span className="Navigation__Item__Arrow pointer-events-none">
                              <ArrowHeaderIcon />
                            </span>
                          )}
                        </MenuDropdown>
                      );
                    }

                    const hasPermission = checkHasPermission(myPermission, item.href);
                    return (
                      <Link
                        key={item.name}
                        to={{
                          pathname: item.link,
                          search: `?place=${searchQuery.place}`,
                        }}
                        className={`Navigation__Item ${item.link === location.pathname ? 'active' : ''} ${isBreakTitle ? 'flex-column' : ''} ${!hasPermission ? 'disabled-link' : ''}`}
                        onClick={onClickLinkItem}
                      >
                        <span className="Navigation__Item__Icon">
                          <IconComponent />
                        </span>

                        {
                          isShowTitle && (
                            <span className="Navigation__Item__Title">{t(item.name)}</span>
                          )
                        }
                      </Link>
                    );
                  })
                }
              </div>
            )
          }
        </div>

        {/* Search Mobile */}
        <div className="d-flex HeaderRight" ref={rightHeaderRef}>
          <LanguageDropdown />

          {/* Fullscreen */}
          <div className="dropdown d-none d-lg-inline-block ms-1">
            <button
              type="button"
              onClick={onToggleFullscreen}
              className="btn header-item noti-icon"
              data-toggle="fullscreen"
              aria-label="Toggle fullscreen mode"
            >
              <i className="bx bx-fullscreen" />
            </button>
          </div>

          {/* eye */}
          {isLocationDetail && (
            <button
              type="button"
              className="EyeButton"
              aria-label="View"
              onClick={onPreviewPlace}
            >
              <EyeIcon />
            </button>
          )}

          {/* Profile */}
          <ProfileMenu />
        </div>
      </div>
    </header>
  );
};

Header.propTypes = {
  myPermission: PropTypes.instanceOf(Object),
  placeDetail: PropTypes.instanceOf(Object),
};

Header.defaultProps = {
  myPermission: {},
  placeDetail: {},
};

export default memo(Header);
